import smartDate from "@qrohlf/smart-date";
import { Button } from "common/Button";
import { Icon } from "common/LegacyIcon";
import { CircleSpinner } from "common/spinners";
import { Map } from "immutable";
import { commonMessages, Msg } from "lib/localization";
import { searchRides } from "lib/searchUtils.js";
import _ from "lodash";
import moment from "moment";
import React from "react";
import { defineMessages, FormattedMessage as T, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { rrMessages } from "../intlMessages";
import { RideList } from "./RideList";
import styles from "./RidePicker.scss";
if (!Intl.ListFormat) {
  require("@formatjs/intl-listformat/polyfill-locales");
}

const messages = defineMessages({
  search: {
    id: "RidePicker.search",
    defaultMessage: "search your rides",
  },
});

class _RidePicker extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      keywords: "",
      searchResults: null,
      searchLoading: null,
      selectedRides: props.selectedRides || Map(),
    };
    this.throttledSearch = _.throttle(this.doSearch, 500, {
      leading: false,
      trailing: true,
    });
  }

  componentDidMount() {
    this.doSearch({ sort_by: "created DESC" });
  }

  doSearch = () => {
    const { currentUser } = this.props;
    const { keywords } = this.state;
    const searchSlug = `${currentUser}/${keywords}`;
    const sortBy = keywords ? "rank DESC" : "created DESC";
    this.setState({ searchLoading: searchSlug });
    searchRides({
      userId: currentUser.id,
      trips: true,
      routes: false,
      public: true,
      is_gps: true,
      keywords,
      sortBy,
    }).then((response) => {
      // guard against race conditions
      if (this.state.searchLoading === searchSlug) {
        // filter out non-gps routes
        const results = response.results.filter((t) => !t.trip.is_stationary);
        this.setState({ searchLoading: null, searchResults: results });
      }
    });
  };

  onKeywordChange = (e) => {
    this.setState({ keywords: e.target.value }, this.throttledSearch);
  };

  onRideClick = (ride) => {
    const { selectedRides } = this.state;
    if (selectedRides.get(ride.id)) {
      this.setState({ selectedRides: selectedRides.delete(ride.id) });
    } else {
      this.setState({ selectedRides: selectedRides.set(ride.id, ride) });
    }
  };

  confirmSelection = () => {
    const { onConfirmSelection } = this.props;
    const { selectedRides } = this.state;
    onConfirmSelection(selectedRides);
  };

  render() {
    const { intl } = this.props;
    const { searchResults, keywords, searchLoading, selectedRides } = this.state;
    const initialSelected = this.props.selectedRides;

    const ridesRemoved = initialSelected && !initialSelected.keySeq().isSubset(selectedRides.keySeq());

    if (!searchLoading && keywords === "" && searchResults && searchResults.length === 0) {
      return noRidesMessage;
    }

    console.log(getTitle(selectedRides, intl));

    return (
      <div>
        <div className={styles.searchContainer}>
          <input
            className={styles.searchBar}
            type="text"
            placeholder={intl.formatMessage(messages.search)}
            value={keywords}
            onChange={this.onKeywordChange}
          />
          {searchLoading && <CircleSpinner size={25} className={styles.searchLoading} />}
        </div>
        {searchResults && (
          <RideList rides={searchResults.map((r) => r.trip)} selected={selectedRides} onRideClick={this.onRideClick} />
        )}
        {searchResults && searchResults.length === 0 && noResultsMessage}
        {searchResults === null && <CircleSpinner size={40} />}
        <h2 className={styles.subheading}>
          <T id="RidePicker.selected" defaultMessage="Selected Rides" />:
        </h2>
        <div>
          {selectedRides
            .map((ride, k) => (
              <div key={k} className={styles.selectedRide}>
                {ride.name}{" "}
                <Icon i="x" onClick={() => this.onRideClick(ride)} style={{ cursor: "pointer", marginLeft: 5 }} />
              </div>
            ))
            .toIndexedSeq()
            .toJS()}
        </div>
        <p className={styles.hint}>
          <T
            id="RidePicker.hint"
            defaultMessage="click one or more of the rides in the list above to add it to your report"
          />
        </p>
        {ridesRemoved && (
          <p className={styles.warning}>
            <T
              id="RidePicker.warning"
              defaultMessage="You have selected to remove some rides from this report. If you remove these rides, all the maps and climbs associated with the rides will be removed as well."
            />
          </p>
        )}
        {selectedRides.size > 0 && (
          <Button onClick={this.confirmSelection} className={styles.goButton} type="success">
            <T id="RidePicker.go" defaultMessage="Go" /> <Icon i="rightChevron" size={15} />
          </Button>
        )}
      </div>
    );
  }
}

export const RidePicker = injectIntl(_RidePicker);

const MultiBike = () => (
  <div className={styles.multibike}>
    <Icon i="rwgps" size={50} />
    <Icon i="rwgps" size={50} />
    <Icon i="rwgps" size={50} />
    <Icon i="rwgps" size={50} style={{ opacity: 0.25 }} />
    <Icon i="rwgps" size={50} />
  </div>
);

export const noResultsMessage = (
  <div className={styles.infoMessage}>
    <MultiBike />
    <h3>
      <Msg m={commonMessages.noResults} />
    </h3>
    <p>
      <T
        id="RidePicker.noResults"
        defaultMessage="We couldn't find any rides that matched that search. Try a different query, or looking at your <a>activities feed</a> to make sure the ride you're looking for exists. Only public routes with GPS data are supported at this time."
        values={{
          a: (...chunks) => <a href="/activities">{chunks}</a>,
        }}
      />
    </p>
  </div>
);

export const noRidesMessage = (
  <div className={styles.infoMessage}>
    <MultiBike />
    <h3>
      <T id="RidePicker.noPublic" defaultMessage="No Public Rides" />
    </h3>
    <p>
      <T
        id="RidePicker.toWrite"
        defaultMessage="To write a ride report, you need to record or upload some rides first!"
      />
    </p>
    <p>
      <T
        id="RidePicker.instructions"
        defaultMessage="You can do that with our <a1>iOS and Android apps</a1>, or by uploading a gps file on the <a2>upload page</a2>."
        values={{
          a1: (...chunks) => <a href="/app">{chunks}</a>,
          a2: (...chunks) => <a href="/log">{chunks}</a>,
        }}
      />
    </p>
    <p>
      <T
        id="RidePicker.onlyPublic"
        defaultMessage="Currently, you can only create a Ride Report from public rides,
      so if you have uploaded a ride but it's not showing on this page try checking
      to make sure that the ride visibility is public."
      />
    </p>
  </div>
);

export const getTitle = (rides, intl) => {
  if (rides.size === 1) {
    const first = rides.valueSeq().first();
    const title = first.name;
    const subtitle = smartDate(first.departed_at);
    return { title, subtitle };
  } else {
    const months = rides
      .valueSeq()
      .map((r) => moment(r.departed_at))
      .sort((a, b) => a > b)
      .map((d) => d.format("MMMM"))
      .toJS();
    const monthString = intl.formatList(_.sortedUniq(months));
    const title = intl.formatMessage(rrMessages.ridesIn, {
      number: rides.size,
      timePeriod: monthString,
    });
    const subtitle = null;
    return { title, subtitle };
  }
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.currentUser,
  };
};

export const RidePickerContainer = connect(mapStateToProps, null)(injectIntl(RidePicker));
